import { useState, useEffect } from "react";
import api from "../services/api";
import logger from "../utils/logger";
import { fromSlug, normalizeBrandNameForMatch } from "../utils/urlSlug";

export const useRestaurantDealsBySlug = (restaurantName) => {
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [restaurantInfo, setRestaurantInfo] = useState(null);

  useEffect(() => {
    if (!restaurantName) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    const fetchRestaurantDeals = async () => {
      try {
        setLoading(true);
        const slugName = fromSlug(restaurantName);

        let brandInfo = null;
        try {
          const findBrandMatch = (brands) =>
            brands.find(
              (brand) =>
                normalizeBrandNameForMatch(brand.name) ===
                normalizeBrandNameForMatch(slugName)
            );

          let brandResponse = await api.get("/brands", {
            params: {
              search: slugName,
              pageSize: 50,
            },
          });

          let brands = brandResponse?.data?.data || [];
          let matchedBrand = findBrandMatch(brands);

          if (!matchedBrand) {
            brandResponse = await api.get("/brands", {
              params: {
                search: restaurantName.replace(/-/g, " "),
                pageSize: 50,
              },
            });
            brands = brandResponse?.data?.data || [];
            matchedBrand = findBrandMatch(brands);
          }

          if (matchedBrand) {
            brandInfo = {
              _id: matchedBrand._id,
              name: matchedBrand.name,
              category: matchedBrand.category,
              cuisine: matchedBrand.cuisine,
              area: matchedBrand.area,
              address: matchedBrand.address,
              logoUrl: matchedBrand.logoUrl,
            };
          }
        } catch (brandError) {
          logger.error("Error fetching brand info:", brandError);
        }

        let dealsData = [];
        try {
          const response = await api.get("/deals", {
            params: {
              brandName: slugName,
              activeOnly: "0",
            },
          });

          dealsData =
            response?.data?.data ||
            response?.data?.docs ||
            response?.data ||
            [];

          if (!cancelled) {
            setDeals(dealsData);
            if (dealsData.length > 0 && dealsData[0].brand) {
              const b = dealsData[0].brand;
              setRestaurantInfo({
                ...b,
                _id: b._id ?? brandInfo?._id,
                name: b.name || brandInfo?.name,
                logoUrl: b.logoUrl || brandInfo?.logoUrl,
                cuisine: b.cuisine ?? brandInfo?.cuisine,
                category: b.category ?? brandInfo?.category,
              });
            } else if (brandInfo) {
              setRestaurantInfo(brandInfo);
            } else {
              setRestaurantInfo(null);
            }
          }
        } catch (dealError) {
          logger.error("Error fetching deals:", dealError);
          if (!cancelled && brandInfo) {
            setRestaurantInfo(brandInfo);
            setDeals([]);
          }
        }
      } catch (error) {
        logger.error("Error fetching restaurant deals:", error);
        if (!cancelled) {
          setDeals([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchRestaurantDeals();
    return () => {
      cancelled = true;
    };
  }, [restaurantName]);

  const activeDeals = deals.filter((deal) => deal.isActive !== false);
  const inactiveDeals = deals.filter((deal) => deal.isActive === false);

  return {
    deals,
    loading,
    restaurantInfo,
    activeDeals,
    inactiveDeals,
  };
};
